import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ThemeProvider";
import { usePermissions } from "@/lib/permissions";
import { useSidebar } from "@/lib/sidebar-context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  LayoutDashboard, Ticket, UserPlus, Users, UsersRound, Package, DollarSign,
  FileText, BarChart3, Star, Settings, LogOut, Menu, X, Sun, Moon,
  Bell, Columns3, AlertCircle, Truck, QrCode, Smartphone, Heart, Receipt, ShoppingCart,
  Building2, ArrowRightLeft, ShoppingBag, CalendarDays, ClipboardList, CalendarRange,
  PanelLeftClose, PanelLeftOpen, Phone, RefreshCw, CreditCard, Clock,
  Check, ChevronDown, MapPin, Megaphone, BadgePercent, LifeBuoy, BookOpen,
  MessageSquare, Shield, Wallet, TabletSmartphone,
} from "lucide-react";
import { useState, useEffect, type ReactNode } from "react";
import QRScanner from "@/components/QRScanner";
import AiHelpWidget from "@/components/shop/AiHelpWidget";

function hexToHsl(hex: string): string | null {
  const m = hex.replace("#", "").match(/.{2}/g);
  if (!m || m.length < 3) return null;
  const r = parseInt(m[0], 16) / 255;
  const g = parseInt(m[1], 16) / 255;
  const b = parseInt(m[2], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getNotificationIcon(type: string) {
  switch (type) {
    case "ticket": return <Ticket className="h-4 w-4 text-blue-500" />;
    case "review": return <Star className="h-4 w-4 text-yellow-500" />;
    case "activity": return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    default: return <Bell className="h-4 w-4 text-muted-foreground" />;
  }
}

interface NavSection {
  label: string;
  items: { href: string; icon: any; label: string; area: string; feature?: string }[];
}

const navSections: NavSection[] = [
  {
    label: "",
    items: [
      { href: "/shop", icon: LayoutDashboard, label: "Dashboard", area: "dashboard" },
    ],
  },
  {
    label: "Tickets",
    items: [
      { href: "/shop/my-queue", icon: ClipboardList, label: "My Queue", area: "tickets", feature: "tickets" },
      { href: "/shop/tickets", icon: Ticket, label: "Tickets", area: "tickets", feature: "tickets" },
      { href: "/shop/kanban", icon: Columns3, label: "Kanban", area: "kanban", feature: "kanban" },
      { href: "/shop/check-in", icon: UserPlus, label: "Check-In", area: "check_in", feature: "check_in" },
      { href: "/shop/appointments", icon: CalendarDays, label: "Appointments", area: "appointments", feature: "appointments" },
    ],
  },
  {
    label: "Sales",
    items: [
      { href: "/shop/pos", icon: DollarSign, label: "Point of Sale", area: "pos_open", feature: "pos" },
      { href: "/shop/quick-price", icon: Phone, label: "Quick Price", area: "quick_price", feature: "pricing" },
      { href: "/shop/invoices", icon: Receipt, label: "Invoices", area: "invoices", feature: "invoices" },
      { href: "/shop/estimates", icon: FileText, label: "Estimates", area: "estimates", feature: "estimates" },
      { href: "/shop/pricing", icon: DollarSign, label: "Service Pricing", area: "pricing", feature: "pricing" },
      { href: "/shop/expenses", icon: Wallet, label: "Expenses", area: "analytics", feature: "analytics" },
    ],
  },
  {
    label: "Customers",
    items: [
      { href: "/shop/customers", icon: Users, label: "Customers", area: "customers", feature: "customers" },
      { href: "/shop/trade-ins", icon: RefreshCw, label: "Trade-Ins", area: "trade_ins", feature: "trade_ins" },
      { href: "/shop/reviews", icon: Star, label: "Reviews", area: "reviews", feature: "reviews" },
      { href: "/shop/loyalty", icon: Heart, label: "Loyalty", area: "loyalty", feature: "loyalty" },
      { href: "/shop/protection-plans", icon: Shield, label: "Protection Plans", area: "protection_plans", feature: "protection_plans" },
      { href: "/shop/guides", icon: BookOpen, label: "Repair Guides", area: "guides", feature: "tickets" },
    ],
  },
  {
    label: "Marketing",
    items: [
      { href: "/shop/sms-outreach", icon: Megaphone, label: "SMS Outreach", area: "sms_outreach", feature: "sms_outreach" },
      { href: "/shop/sms-inbox", icon: MessageSquare, label: "SMS Inbox", area: "sms_outreach", feature: "sms_outreach" },
      { href: "/shop/coupons", icon: BadgePercent, label: "Coupons", area: "pos_discounts", feature: "pos" },
    ],
  },
  {
    label: "Inventory",
    items: [
      { href: "/shop/inventory", icon: Package, label: "Inventory", area: "inventory", feature: "inventory" },
      { href: "/shop/loaner-devices", icon: TabletSmartphone, label: "Loaner Devices", area: "inventory", feature: "inventory" },
      { href: "/shop/vendors", icon: Truck, label: "Vendors", area: "vendors", feature: "vendors" },
      { href: "/shop/purchase-orders", icon: ShoppingBag, label: "Purchase Orders", area: "purchase_orders", feature: "purchase_orders" },
      { href: "/shop/transfers", icon: ArrowRightLeft, label: "Transfers", area: "inventory_transfers", feature: "multi_location" },
    ],
  },
  {
    label: "Team",
    items: [
      { href: "/shop/staff", icon: UsersRound, label: "Staff", area: "staff", feature: "staff" },
      { href: "/shop/staff-schedule", icon: CalendarRange, label: "Staff Schedule", area: "staff_schedule", feature: "staff_schedule" },
      { href: "/shop/analytics", icon: BarChart3, label: "Analytics", area: "analytics", feature: "analytics" },
      { href: "/shop/eod-report", icon: ClipboardList, label: "End of Day", area: "eod_report", feature: "eod_report" },
    ],
  },
  {
    label: "Settings",
    items: [
      { href: "/shop/voice-assistant", icon: Phone, label: "Voice Assistant", area: "voice_assistant", feature: "voice_ai" },
      { href: "/shop/templates", icon: FileText, label: "Templates", area: "templates", feature: "templates" },
      { href: "/shop/catalog", icon: Smartphone, label: "Device Catalog", area: "device_catalog", feature: "device_catalog" },
      { href: "/shop/multi-location", icon: Building2, label: "Franchise Groups", area: "multi_location", feature: "multi_location" },
      { href: "/shop/billing", icon: CreditCard, label: "Billing", area: "settings" },
      { href: "/shop/settings", icon: Settings, label: "Shop Settings", area: "settings" },
    ],
  },
  {
    label: "Help",
    items: [
      { href: "/shop/help", icon: BookOpen, label: "Platform Guide", area: "dashboard" },
      { href: "/shop/support", icon: LifeBuoy, label: "Support", area: "dashboard" },
    ],
  },
];

export default function ShopLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [location, navigate] = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { collapsed, hidden, setCollapsed } = useSidebar();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [qrScanOpen, setQrScanOpen] = useState(false);

  const { data: shops } = useQuery<any[]>({ queryKey: ["/api/my-shops"] });
  const [selectedShopId, setSelectedShopId] = useState<number | null>(() => {
    const saved = localStorage.getItem("ifixable_selected_shop_id");
    return saved ? parseInt(saved, 10) : null;
  });
  const [locationSwitcherOpen, setLocationSwitcherOpen] = useState(false);

  // Keep selection valid as shops list loads/changes
  useEffect(() => {
    if (!shops || shops.length === 0) return;
    if (!selectedShopId || !shops.find(s => s.id === selectedShopId)) {
      setSelectedShopId(shops[0].id);
    }
  }, [shops]);

  const currentShop = (shops && selectedShopId
    ? shops.find(s => s.id === selectedShopId) ?? shops[0]
    : shops?.[0]) ?? undefined;

  const switchShop = (shopId: number) => {
    setSelectedShopId(shopId);
    localStorage.setItem("ifixable_selected_shop_id", String(shopId));
    setLocationSwitcherOpen(false);
  };

  const { hasPermission } = usePermissions(currentShop?.id);

  const { data: packages } = useQuery<any[]>({ queryKey: ["/api/subscription-packages"] });
  // Use effectiveTier (from group billing) if available, fallback to shop's own tier
  const effectiveTier = currentShop?.effectiveTier || currentShop?.subscriptionTier || "free";

  const brandColor: string | null = currentShop?.brandColor ?? null;
  const logoUrl: string | null = currentShop?.logoUrl ?? null;
  const isWhiteLabel = effectiveTier === "enterprise" && (!!brandColor || !!logoUrl);

  useEffect(() => {
    if (!brandColor) return;
    const hsl = hexToHsl(brandColor);
    if (!hsl) return;
    const root = document.documentElement;
    const prev = {
      primary: root.style.getPropertyValue("--primary"),
      ring: root.style.getPropertyValue("--ring"),
    };
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--ring", hsl);
    return () => {
      root.style.setProperty("--primary", prev.primary);
      root.style.setProperty("--ring", prev.ring);
    };
  }, [brandColor]);

  const currentPackage = (packages || []).find((p: any) => p.tier === effectiveTier);
  const packageFeatures: string[] = Array.isArray(currentPackage?.features) ? currentPackage.features : [];
  // featureKeys contains the raw DB keys (e.g. "sms_outreach") used for gating.
  // Falls back to packageFeatures for backward compatibility with old server responses.
  const packageFeatureKeys: string[] = Array.isArray(currentPackage?.featureKeys) ? currentPackage.featureKeys : packageFeatures;
  const allFeaturesEnabled = !currentPackage || packageFeatureKeys.length === 0 || effectiveTier === "enterprise";
  const hasFeature = (feature?: string) => !feature || allFeaturesEnabled || packageFeatureKeys.includes(feature);

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ["/api/shops", currentShop?.id, "notifications"],
    enabled: !!currentShop?.id,
    refetchInterval: 30000,
  });

  const notifClearedKey = `notif_cleared_${currentShop?.id}`;
  const notifDismissedKey = `notif_dismissed_${currentShop?.id}`;

  const [clearedBefore, setClearedBefore] = useState<string>(() =>
    (currentShop?.id ? localStorage.getItem(`notif_cleared_${currentShop.id}`) : null) || ""
  );
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    const raw = currentShop?.id ? localStorage.getItem(`notif_dismissed_${currentShop.id}`) : null;
    return raw ? new Set(JSON.parse(raw)) : new Set();
  });

  // Re-sync from localStorage when shop changes
  useEffect(() => {
    if (!currentShop?.id) return;
    setClearedBefore(localStorage.getItem(`notif_cleared_${currentShop.id}`) || "");
    const raw = localStorage.getItem(`notif_dismissed_${currentShop.id}`);
    setDismissedIds(raw ? new Set(JSON.parse(raw)) : new Set());
  }, [currentShop?.id]);

  const visibleNotifications = (notifications || []).filter((n) => {
    if (dismissedIds.has(n.id)) return false;
    if (clearedBefore && new Date(n.timestamp) <= new Date(clearedBefore)) return false;
    return true;
  });

  const clearAllNotifications = () => {
    const now = new Date().toISOString();
    setClearedBefore(now);
    setDismissedIds(new Set());
    if (currentShop?.id) {
      localStorage.setItem(notifClearedKey, now);
      localStorage.removeItem(notifDismissedKey);
    }
  };

  const dismissNotification = (id: string) => {
    const next = new Set(dismissedIds);
    next.add(id);
    setDismissedIds(next);
    if (currentShop?.id) {
      localStorage.setItem(notifDismissedKey, JSON.stringify([...next]));
    }
  };

  const notifCount = visibleNotifications.length;

  const isActive = (href: string) =>
    href === "/shop"
      ? location === "/shop" || location === "/shop/"
      : location.startsWith(href);

  if (hidden) {
    return (
      <div className="min-h-screen bg-background">
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-sidebar border-r border-sidebar-border transition-all duration-200 ${
          collapsed ? "w-14" : "w-64"
        }`}
      >
        <div className={`h-16 flex items-center border-b border-sidebar-border shrink-0 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          {!collapsed && (
            <Link href="/">
              {isWhiteLabel && logoUrl ? (
                <img src={logoUrl} alt="logo" className="h-8 max-w-[140px] object-contain cursor-pointer" data-testid="logo-sidebar" />
              ) : isWhiteLabel && currentShop?.name ? (
                <span className="text-xl font-bold text-primary cursor-pointer truncate max-w-[140px]" data-testid="logo-sidebar">{currentShop.name}</span>
              ) : (
                <span className="text-xl font-bold text-primary cursor-pointer" data-testid="logo-sidebar">iFixable</span>
              )}
            </Link>
          )}
          {collapsed && (
            <Link href="/">
              {isWhiteLabel && logoUrl ? (
                <img src={logoUrl} alt="logo" className="h-7 w-7 object-contain cursor-pointer rounded" />
              ) : (
                <span className="text-lg font-bold text-primary cursor-pointer">{isWhiteLabel ? (currentShop?.name?.charAt(0) || "iF") : "iF"}</span>
              )}
            </Link>
          )}
          {!collapsed && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCollapsed(true)} data-testid="button-collapse-sidebar" title="Collapse sidebar">
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          )}
        </div>

        {!collapsed && currentShop && (
          <div className="px-3 py-2.5 border-b border-sidebar-border shrink-0">
            {shops && shops.length > 1 ? (
              <Popover open={locationSwitcherOpen} onOpenChange={setLocationSwitcherOpen}>
                <PopoverTrigger asChild>
                  <button className="w-full text-left rounded-md px-2 py-1.5 -mx-2 hover:bg-sidebar-accent/40 transition-colors group" data-testid="button-location-switcher">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-primary/15 flex items-center justify-center shrink-0">
                        <MapPin className="h-3 w-3 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-sidebar-foreground truncate leading-tight" data-testid="text-shop-name">{currentShop.name}</p>
                        <p className="text-[10px] text-muted-foreground">{shops.length} locations</p>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 group-hover:text-foreground transition-colors" />
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-60 p-2" align="start" side="right" sideOffset={8}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">Switch Location</p>
                  <div className="space-y-0.5">
                    {shops.map(s => (
                      <button
                        key={s.id}
                        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors ${s.id === currentShop.id ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent text-foreground"}`}
                        onClick={() => switchShop(s.id)}
                        data-testid={`location-option-${s.id}`}
                      >
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${s.id === currentShop.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                          {s.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="truncate">{s.name}</p>
                          {s.city && <p className="text-[10px] text-muted-foreground truncate">{s.city}</p>}
                        </div>
                        {s.id === currentShop.id && <Check className="h-4 w-4 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="px-1">
                <p className="text-sm font-medium text-sidebar-foreground truncate" data-testid="text-shop-name">{currentShop.name}</p>
                <Badge variant="outline" className={`text-[10px] mt-0.5 capitalize ${effectiveTier === "enterprise" ? "border-amber-400 text-amber-600 dark:text-amber-400" : ""}`}>
                  {effectiveTier} Plan
                  {effectiveTier !== currentShop.subscriptionTier && <span className="ml-1 opacity-60">(group)</span>}
                </Badge>
              </div>
            )}
          </div>
        )}
        {collapsed && currentShop && (
          <div className="py-2 border-b border-sidebar-border shrink-0 flex justify-center">
            {shops && shops.length > 1 ? (
              <Popover open={locationSwitcherOpen} onOpenChange={setLocationSwitcherOpen}>
                <PopoverTrigger asChild>
                  <button className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors relative" title={`${currentShop.name} — click to switch location`} data-testid="button-location-switcher-collapsed">
                    <span className="text-[10px] font-bold text-primary">{currentShop.name?.charAt(0)?.toUpperCase()}</span>
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-60 p-2" align="start" side="right" sideOffset={8}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">Switch Location</p>
                  <div className="space-y-0.5">
                    {shops.map(s => (
                      <button
                        key={s.id}
                        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors ${s.id === currentShop.id ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent text-foreground"}`}
                        onClick={() => switchShop(s.id)}
                        data-testid={`location-option-collapsed-${s.id}`}
                      >
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${s.id === currentShop.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                          {s.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="truncate">{s.name}</p>
                          {s.city && <p className="text-[10px] text-muted-foreground truncate">{s.city}</p>}
                        </div>
                        {s.id === currentShop.id && <Check className="h-4 w-4 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center" title={currentShop.name}>
                <span className="text-[10px] font-bold text-primary">{currentShop.name?.charAt(0)}</span>
              </div>
            )}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navSections.map((section, sIdx) => {
            const visibleItems = section.items.filter((item) => {
              if (item.area === "dashboard") return true;
              if (!hasFeature(item.feature)) return false;
              return hasPermission(item.area, "view");
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={sIdx} className="mb-3">
                {!collapsed && section.label && (
                  <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                    {section.label}
                  </p>
                )}
                {visibleItems.map(({ href, icon: Icon, label }) => (
                  <Link key={href} href={href}>
                    <div
                      className={`flex items-center gap-3 rounded-md cursor-pointer transition-colors mb-0.5 ${
                        collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2"
                      } ${
                        isActive(href)
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                      }`}
                      data-testid={`nav-${label.toLowerCase().replace(/[\s&/]+/g, "-")}`}
                      title={collapsed ? label : undefined}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span className="truncate text-sm">{label}</span>}
                    </div>
                  </Link>
                ))}
                {collapsed && <div className="my-1 border-t border-sidebar-border/40" />}
              </div>
            );
          })}
        </nav>

        <div className={`border-t border-sidebar-border shrink-0 ${collapsed ? "p-2" : "p-4"}`}>
          {!collapsed ? (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                {user?.fullName?.charAt(0) || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.fullName}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary" title={user?.fullName || ""}>
                {user?.fullName?.charAt(0) || "?"}
              </div>
            </div>
          )}
          <Button variant="ghost" size={collapsed ? "icon" : "sm"} className={collapsed ? "w-full" : "w-full justify-start"} onClick={logout} data-testid="button-logout" title="Log Out">
            <LogOut className={collapsed ? "h-4 w-4" : "h-4 w-4 mr-2"} />
            {!collapsed && "Log Out"}
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border flex flex-col transform transition-transform duration-200 lg:hidden ${
          mobileSidebarOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border shrink-0">
          <Link href="/">
            {isWhiteLabel && logoUrl ? (
              <img src={logoUrl} alt="logo" className="h-8 max-w-[130px] object-contain cursor-pointer" />
            ) : isWhiteLabel && currentShop?.name ? (
              <span className="text-xl font-bold text-primary cursor-pointer truncate max-w-[130px]">{currentShop.name}</span>
            ) : (
              <span className="text-xl font-bold text-primary cursor-pointer">iFixable</span>
            )}
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setMobileSidebarOpen(false)}><X className="h-4 w-4" /></Button>
        </div>
        {currentShop && (
          <div className="px-3 py-2.5 border-b border-sidebar-border shrink-0">
            {shops && shops.length > 1 ? (
              <Popover open={locationSwitcherOpen} onOpenChange={setLocationSwitcherOpen}>
                <PopoverTrigger asChild>
                  <button className="w-full text-left rounded-md px-2 py-1.5 -mx-2 hover:bg-sidebar-accent/40 transition-colors group" data-testid="button-location-switcher-mobile">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-primary/15 flex items-center justify-center shrink-0">
                        <MapPin className="h-3 w-3 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-sidebar-foreground truncate leading-tight">{currentShop.name}</p>
                        <p className="text-[10px] text-muted-foreground">{shops.length} locations · tap to switch</p>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-60 p-2" align="start" side="bottom" sideOffset={4}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">Switch Location</p>
                  <div className="space-y-0.5">
                    {shops.map(s => (
                      <button
                        key={s.id}
                        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors ${s.id === currentShop.id ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent text-foreground"}`}
                        onClick={() => { switchShop(s.id); setMobileSidebarOpen(false); }}
                        data-testid={`location-option-mobile-${s.id}`}
                      >
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${s.id === currentShop.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                          {s.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="truncate">{s.name}</p>
                          {s.city && <p className="text-[10px] text-muted-foreground truncate">{s.city}</p>}
                        </div>
                        {s.id === currentShop.id && <Check className="h-4 w-4 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="px-1">
                <p className="text-sm font-medium text-sidebar-foreground truncate">{currentShop.name}</p>
                <Badge variant="outline" className="text-[10px] mt-0.5 capitalize">{currentShop.subscriptionTier} Plan</Badge>
              </div>
            )}
          </div>
        )}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navSections.map((section, sIdx) => {
            const visibleItems = section.items.filter((item) => {
              if (item.area === "dashboard") return true;
              if (!hasFeature(item.feature)) return false;
              return hasPermission(item.area, "view");
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={sIdx} className="mb-4">
                {section.label && <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{section.label}</p>}
                {visibleItems.map(({ href, icon: Icon, label }) => (
                  <Link key={href} href={href}>
                    <div
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors mb-0.5 ${
                        isActive(href) ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                      }`}
                      onClick={() => setMobileSidebarOpen(false)}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{label}</span>
                    </div>
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-sidebar-border shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{user?.fullName?.charAt(0) || "?"}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.fullName}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={logout}><LogOut className="h-4 w-4 mr-2" /> Log Out</Button>
        </div>
      </aside>

      {mobileSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />}

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ${collapsed ? "lg:ml-14" : "lg:ml-64"}`}>
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 lg:px-6 shrink-0 sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileSidebarOpen(true)} data-testid="button-menu">
              <Menu className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setCollapsed(!collapsed)} data-testid="button-toggle-sidebar" title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
            {isWhiteLabel && logoUrl ? (
              <img src={logoUrl} alt="logo" className="lg:hidden h-6 max-w-[100px] object-contain" />
            ) : (
              <span className="lg:hidden text-sm font-semibold text-primary">{isWhiteLabel && currentShop?.name ? currentShop.name : "iFixable"}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Link href="/shop/pos">
              <Button variant="ghost" size="icon" title="Point of Sale" data-testid="button-pos-shortcut">
                <ShoppingCart className="h-4 w-4" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setQrScanOpen(true)} title="Scan QR Code" data-testid="button-qr-scan">
              <QrCode className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="button-theme">
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="button-notifications">
                  <Bell className="h-4 w-4" />
                  {notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground" data-testid="badge-notification-count">
                      {notifCount > 9 ? "9+" : notifCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0" data-testid="dropdown-notifications">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h4 className="text-sm font-semibold" data-testid="text-notifications-title">Notifications</h4>
                  {visibleNotifications.length > 0 && (
                    <button
                      onClick={clearAllNotifications}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="button-clear-all-notifications"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {visibleNotifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="text-no-notifications">No recent notifications</div>
                  ) : (
                    visibleNotifications.map((n) => (
                      <div key={n.id} className="flex items-start gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 group" data-testid={`notification-item-${n.id}`}>
                        <div className="mt-0.5 shrink-0">{getNotificationIcon(n.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium" data-testid={`text-notification-title-${n.id}`}>{n.title}</p>
                            <Badge variant="secondary" className="text-[10px]" data-testid={`badge-notification-type-${n.id}`}>{n.type}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2" data-testid={`text-notification-message-${n.id}`}>{n.message}</p>
                          <p className="text-[10px] text-muted-foreground mt-1" data-testid={`text-notification-time-${n.id}`}>{timeAgo(n.timestamp)}</p>
                        </div>
                        <button
                          onClick={() => dismissNotification(n.id)}
                          className="mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                          data-testid={`button-dismiss-notification-${n.id}`}
                          title="Dismiss"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {currentShop?.status === "pending" && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-800 px-4 py-3" data-testid="banner-pending-shop">
              <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Shop Pending Approval</p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                  Your shop is awaiting admin approval. You can explore and configure the platform now — everything will go live once approved.
                </p>
              </div>
            </div>
          )}
          {currentShop?.status === "paused" && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800 px-4 py-3" data-testid="banner-paused-shop">
              <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-orange-800 dark:text-orange-300">Account Temporarily Paused</p>
                <p className="text-xs text-orange-700 dark:text-orange-400 mt-0.5">
                  Your shop has been paused by an administrator. Customer-facing pages and API access are currently disabled. Please contact support to resume your account.
                </p>
              </div>
            </div>
          )}
          {children}
        </main>
      </div>

      {(location === "/shop/help" || location === "/shop/support") && <AiHelpWidget />}

      {qrScanOpen && (
        <QRScanner
          onResult={(text) => {
            setQrScanOpen(false);
            const clean = text.trim();
            if (clean.startsWith("http")) {
              try { navigate(new URL(clean).pathname); } catch { navigate(`/track/${clean}`); }
            } else if (clean.startsWith("TKT-") || clean.startsWith("INV-")) {
              navigate(`/shop/tickets?search=${encodeURIComponent(clean)}`);
            } else {
              navigate(`/track/${clean}`);
            }
          }}
          onClose={() => setQrScanOpen(false)}
        />
      )}
    </div>
  );
}
